<script setup>
import { computed, ref } from 'vue';
import { ensureLocale, applyLocale } from 'dashboard/i18n/localeLoader';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { useUISettings } from 'dashboard/composables/useUISettings';

import FormSelect from 'v3/components/Form/Select.vue';

defineProps({
  label: { type: String, default: '' },
  description: { type: String, default: '' },
});

const localeComposer = useI18n({ useScope: 'global' });
const { t } = localeComposer;
const store = useStore();
const isUpdatingLanguage = ref(false);
const { uiSettings } = useUISettings();
const { enabledLanguages } = useConfig();
const { currentAccount } = useAccount();

const currentLanguage = computed(() => uiSettings.value?.locale ?? '');

const languageOptions = computed(() => [
  {
    name: t(
      'PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.USE_ACCOUNT_DEFAULT'
    ),
    iso_639_1_code: '',
  },
  ...(enabledLanguages ?? []),
]);

const updateLanguage = async languageCode => {
  if (isUpdatingLanguage.value) return;
  isUpdatingLanguage.value = true;
  try {
    if (languageCode && !(enabledLanguages || []).some(l => l.iso_639_1_code === languageCode)) {
      throw new Error('Unsupported interface language');
    }
    const effectiveLocale = languageCode || currentAccount.value.locale;
    // A missing chunk must never persist a preference that cannot be rendered.
    const loaded = await ensureLocale(localeComposer, effectiveLocale);
    await store.dispatch('updateInterfaceLocale', { locale: languageCode || null });
    await applyLocale(localeComposer, loaded);
    useAlert(t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_SUCCESS'));
  } catch {
    useAlert(t('PROFILE_SETTINGS.FORM.INTERFACE_SECTION.LANGUAGE.UPDATE_ERROR'));
  } finally {
    isUpdatingLanguage.value = false;
  }
};

const selectedValue = computed({
  get: () => currentLanguage.value,
  set: value => {
    updateLanguage(value);
  },
});
</script>

<template>
  <div class="flex gap-2 justify-between w-full items-start">
    <div>
      <label class="text-n-gray-12 font-medium leading-6 text-sm">
        {{ label }}
      </label>
      <p class="text-n-gray-11">
        {{ description }}
      </p>
    </div>
    <FormSelect
      v-model="selectedValue"
      :disabled="isUpdatingLanguage"
      name="language"
      spacing="compact"
      class="min-w-28 mt-px"
      :options="languageOptions"
      label=""
    >
      <option
        v-for="option in languageOptions"
        :key="option.iso_639_1_code || 'default'"
        :value="option.iso_639_1_code"
        :selected="option.iso_639_1_code === selectedValue"
      >
        {{ option.name }}
      </option>
    </FormSelect>
  </div>
</template>
